import ApiHelper from '~/client/src/shared/utils/ApiHelper'

import { GET_USERS_FROM_PROCORE } from '../stores/EventStore/eventConstants'
import InitialState from '../stores/InitialState'

interface IToken {
  _t: string
  AccessToken: string
  TokenType: string
  ExpiresIn: number
  RefreshToken: string
  CreatedAt: number
}

export interface IProcoreUser {
  id: number
  businessPhone: string
  email: string
  firstName: string
  jobTitle: string
  lastName: string
  mobilePhone: string
  name: string
  struxhubProjects: string[]
  vendor?: {
    name: string
  }
}

export interface IProcoreProjectWithUsers {
  id: string
  display_name: string
  users?: IProcoreUser[]
}

export interface IProcoreCompanyWithProjectsAndUsers {
  name: string
  projects: IProcoreProjectWithUsers[]
}

const PROCORE_BASE_URL = `${location.protocol}//${location.host}`
export interface IProcoreUserBasic {
  id: string
  login: string
  name: string
}

export interface IProcoreProject {
  id: string
  name: string
  isActive: boolean
}

export interface IProcoreCompany {
  id: string
  name: string
  isActive: boolean
  projects: IProcoreProject[]
}

export default class ProcoreService {
  private token: IToken = null

  private get tokenUrl() {
    return PROCORE_BASE_URL + '/procore/Auth/token'
  }

  private get inviteKeyUrl() {
    return PROCORE_BASE_URL + '/procore/Auth/invitekey'
  }

  private get allProjectsUrl() {
    return PROCORE_BASE_URL + '/procore/Project/all'
  }

  private get permittedProjectsUrl() {
    return PROCORE_BASE_URL + '/procore/Project/permitted'
  }

  private get usersByProjectsUrl() {
    return PROCORE_BASE_URL + '/procore/Project/users'
  }

  private get authMeUrl() {
    return PROCORE_BASE_URL + '/procore/Auth/me'
  }

  private get logoutUrl() {
    return PROCORE_BASE_URL + '/procore/Auth/logout'
  }

  public constructor(private state: InitialState) {}

  public async login() {
    this.token = null
    const { RefreshToken } = await this.getToken()
    return this.getInviteKeyFromProcoreToken(RefreshToken)
  }

  public async getProcoreToken() {
    return await this.getToken()
  }

  public async getPopulatedCompanies() {
    const { AccessToken, RefreshToken } = await this.getToken()

    this.state.loading.set(GET_USERS_FROM_PROCORE, true)
    const companies = await ApiHelper.fetchJson(this.allProjectsUrl, {
      method: 'GET',
      mode: 'cors',
      headers: {
        Code: RefreshToken,
        Token: AccessToken,
      },
    })

    const projectIds = []
    companies.forEach(company => {
      company.projects = company.projects || []
      projectIds.push(...company.projects.map(p => p.id))
    })

    const usersByProjects = await this.getUsersForProjects(projectIds)

    companies.forEach(company => {
      company.projects.forEach(project => {
        const keyValPair = usersByProjects.find(({ key }) => key === project.id)
        project.users = keyValPair && keyValPair.value
      })
    })

    return companies
  }

  private async getUsersForProjects(projectIds: string[]) {
    const { AccessToken, RefreshToken } = await this.getToken()

    const urlParams = '?' + projectIds.map(id => 'projectsids=' + id).join('&')
    return ApiHelper.fetchJson(this.usersByProjectsUrl + urlParams, {
      method: 'GET',
      mode: 'cors',
      headers: {
        Code: RefreshToken,
        Token: AccessToken,
      },
    })
  }

  private async getToken(): Promise<IToken> {
    return (
      this.token ||
      (this.token = await new Promise((resolve, reject) => {
        let popup: Window = null
        let intervalId: number = null

        const cleanup = () => {
          window.removeEventListener('message', onMessage)
          if (intervalId !== null) {
            clearInterval(intervalId)
            intervalId = null
          }
        }

        const onMessage = ({ data: { data, message } }) => {
          if (message === 'procoreToken') {
            cleanup()
            popup?.close()
            resolve(data)
          }
        }

        window.removeEventListener('message', onMessage)
        window.addEventListener('message', onMessage)

        popup = window.open(this.tokenUrl, 'Procore', 'height=600,width=400')

        if (!popup) {
          cleanup()
          reject(new Error('Popup was blocked by the browser'))
          return
        }

        intervalId = window.setInterval(() => {
          if (popup?.closed) {
            cleanup()
            reject(new Error('Procore login popup closed by user'))
          }
        }, 300)
      }))
    )
  }

  private getInviteKeyFromProcoreToken(code: string) {
    return ApiHelper.fetchJson(this.inviteKeyUrl, {
      method: 'GET',
      mode: 'cors',
      headers: {
        Code: code,
      },
    })
  }

  public async getProcoreUserMe(companyId: string): Promise<IProcoreUserBasic> {
    const rawData = await ApiHelper.fetchJson(
      `${this.authMeUrl}?companyId=${companyId}`,
      {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
      },
    )

    return {
      id: String(rawData.id),
      login: rawData.login,
      name: rawData.name,
    }
  }

  public async getPermittedCompaniesWithProjects(): Promise<IProcoreCompany[]> {
    const rawData = await ApiHelper.fetchJson(this.permittedProjectsUrl, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
    })

    return this.transformCompaniesData(rawData)
  }

  private transformCompaniesData(rawData: any[]): IProcoreCompany[] {
    return rawData
      .filter(company => company.is_active)
      .map(company => ({
        id: String(company.id),
        name: company.name,
        isActive: company.is_active,
        projects:
          company.projects
            ?.filter((project: any) => project.active)
            ?.map((project: any) => ({
              id: String(project.id),
              name: project.name,
              isActive: project.active,
            })) || [],
      }))
  }

  public async logoutFromProcore() {
    this.token = null

    return ApiHelper.fetchJson(this.logoutUrl, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
    })
  }
}
